/**
 * WerkstattKundenPage — Werkstatt-Kundenverwaltung
 *
 * Listet alle CRM-Kontakte mit `betrieb='werkstatt'`.
 * Suche (Name / E-Mail / Telefon, debounced), Filter nach Stammkunde-Status und aktiven Labels.
 * Anlegen via Kontakt-Picker (bestehende `crm_kontakte`) oder „Neuen Kontakt anlegen" — Label
 * „Werkstatt-Kunde" + „LCS GmbH" werden beim Anlegen automatisch gesetzt.
 *
 * Issue #401 · Parent #322 — Multi-Mandant Werkstatt-CRM.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Wrench, Search, Plus, Star, Users, Phone, Mail,
  ChevronRight, X, CheckCircle2, Tag, Loader2, UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type { CRMKontakt } from '@/hooks/useCRM';
import { useCRMFirmen } from '@/hooks/useCRM';
import NeuerKontaktDialog from '@/components/NeuerKontaktDialog';
import { LCS_BETRIEB_NAME } from '@shared/const';

const WERKSTATT_LABELS = ['Werkstatt-Kunde', LCS_BETRIEB_NAME] as const;
const STAMMKUNDE_TAG = 'Stammkunde';

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function mergeTags(existing: string[] | null | undefined, extra: readonly string[]): string[] {
  const set = new Set(existing || []);
  extra.forEach(t => set.add(t));
  return Array.from(set);
}

function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Dialog: Neuer Werkstatt-Kunde (Kontakt-Picker ODER Neuer Kontakt)
// ---------------------------------------------------------------------------

interface NeuerWerkstattKundeDialogProps {
  open: boolean;
  onClose: () => void;
  onAssignExisting: (kontaktId: string) => Promise<void>;
  onCreateNew: (data: Partial<CRMKontakt>) => Promise<void>;
}

function NeuerWerkstattKundeDialog({ open, onClose, onAssignExisting, onCreateNew }: NeuerWerkstattKundeDialogProps) {
  const [mode, setMode] = useState<'picker' | 'new'>('picker');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);
  const [results, setResults] = useState<CRMKontakt[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);
  const { firmen } = useCRMFirmen();

  useEffect(() => {
    if (!open) {
      setMode('picker');
      setSearch('');
      setResults([]);
      setAssigning(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== 'picker') return;
    const q = debouncedSearch.trim();
    if (q.length < 2) { setResults([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('crm_kontakte')
        .select('id, name, firma, email, telefon, tags, betrieb, kategorie, favorit, erstellt_am, aktualisiert_am')
        .or(`name.ilike.%${q}%,email.ilike.%${q}%,firma.ilike.%${q}%,telefon.ilike.%${q}%`)
        .limit(20);
      if (cancelled) return;
      if (error) {
        console.error('Kontakt-Suche:', error);
        setResults([]);
      } else {
        setResults((data || []) as CRMKontakt[]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, open, mode]);

  if (!open) return null;

  const handleAssign = async (k: CRMKontakt) => {
    setAssigning(k.id);
    try {
      await onAssignExisting(k.id);
      onClose();
    } catch (e: any) {
      toast.error(`Fehler: ${e.message || 'Zuordnung fehlgeschlagen'}`);
    } finally {
      setAssigning(null);
    }
  };

  const handleCreate = async (data: Partial<CRMKontakt>) => {
    await onCreateNew(data);
    onClose();
  };

  return (
    <>
      {mode === 'new' && (
        <NeuerKontaktDialog
          open={true}
          onClose={() => setMode('picker')}
          onCreate={handleCreate}
          firmen={firmen}
        />
      )}

      {mode === 'picker' && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
          <div className="relative w-full sm:max-w-lg bg-white sm:rounded-2xl shadow-2xl flex flex-col max-h-[100dvh] sm:max-h-[85vh] h-full sm:h-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#D97706] flex items-center justify-center">
                  <Wrench className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900">Neuer Werkstatt-Kunde</h2>
                  <p className="text-xs text-gray-400">Kontakt auswählen oder neu anlegen</p>
                </div>
              </div>
              <button onClick={onClose} aria-label="Schließen" className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            <div className="px-5 py-3 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Bestehenden Kontakt suchen (Name, E-Mail, Telefon)"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:bg-white focus:border-[#D97706]/40 focus:outline-none"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3">
              {search.trim().length < 2 ? (
                <div className="text-center py-10 text-gray-400">
                  <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Tippen Sie mindestens 2 Zeichen, um Kontakte zu suchen.</p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : results.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <p className="text-sm">Keine passenden Kontakte gefunden.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {results.map(k => {
                    const isWerkstatt = k.betrieb === 'werkstatt';
                    return (
                      <button
                        key={k.id}
                        disabled={assigning === k.id}
                        onClick={() => handleAssign(k)}
                        className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-transparent hover:bg-gray-50 hover:border-gray-200 text-left transition-colors disabled:opacity-50"
                      >
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold bg-[#D97706] flex-shrink-0">
                          {getInitials(k.name)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold text-gray-900 truncate">{k.name}</span>
                            {isWerkstatt && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                                bereits Werkstatt
                              </span>
                            )}
                          </div>
                          {k.firma && <div className="text-[11px] text-gray-500 truncate">{k.firma}</div>}
                          <div className="text-[10px] text-gray-400 truncate">{[k.email, k.telefon].filter(Boolean).join(' · ')}</div>
                        </div>
                        {assigning === k.id ? (
                          <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-300" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-5 py-4 border-t border-gray-100 bg-white sm:rounded-b-2xl">
              <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:bg-gray-100">
                Abbrechen
              </button>
              <button
                onClick={() => setMode('new')}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#D97706] hover:bg-[#B45309] transition-colors"
              >
                <UserPlus className="w-4 h-4" />
                Neuen Kontakt anlegen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared helper: crm_kontakt_labels + kontakt_werkstatt_metadata
// ---------------------------------------------------------------------------

async function setzeWerkstattLabelsUndMetadata(kontaktId: string): Promise<void> {
  // Nach Migration 20260425_betriebe_struktur_holding ist 'Werkstatt Koop'
  // deprecated; Werkstatt firmiert als Marke (marken.typ='werkstatt') unter LCS GmbH.
  const { data: lcsBetrieb } = await supabase
    .from('betriebe')
    .select('id')
    .eq('name', LCS_BETRIEB_NAME)
    .eq('deprecated', false)
    .maybeSingle();

  const { data: werkstattMarke } = await supabase
    .from('marken')
    .select('id')
    .eq('marken_name', 'Werkstatt')
    .maybeSingle();

  const labelRows: Array<{ kontakt_id: string; betrieb_id: string; marke_id: string | null; rolle: string; aktiv: boolean }> = [];
  if (lcsBetrieb && werkstattMarke) {
    labelRows.push({ kontakt_id: kontaktId, betrieb_id: (lcsBetrieb as any).id, marke_id: (werkstattMarke as any).id, rolle: 'kunde', aktiv: true });
  }
  if (lcsBetrieb) {
    labelRows.push({ kontakt_id: kontaktId, betrieb_id: (lcsBetrieb as any).id, marke_id: null, rolle: 'kunde', aktiv: true });
  }
  if (labelRows.length > 0) {
    await supabase
      .from('crm_kontakt_labels')
      .upsert(labelRows, { onConflict: 'kontakt_id,betrieb_id,marke_id,rolle' });
  }
  await supabase
    .from('kontakt_werkstatt_metadata')
    .upsert({ kontakt_id: kontaktId }, { onConflict: 'kontakt_id' });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function WerkstattKundenPage() {
  const [kunden, setKunden] = useState<CRMKontakt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [stammkundeOnly, setStammkundeOnly] = useState(false);
  const [activeLabels, setActiveLabels] = useState<string[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = () => setRefreshKey(k => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error: err } = await supabase
        .from('crm_kontakte')
        .select('*')
        .eq('betrieb', 'werkstatt')
        .order('name');
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setKunden([]);
      } else {
        setError(null);
        setKunden((data || []) as CRMKontakt[]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // Sammle Tag-Vorkommen für Filter-Pills (ohne die impliziten Werkstatt-Labels)
  const labelCounts = useMemo(() => {
    const map = new Map<string, number>();
    kunden.forEach(k => (k.tags || []).forEach(t => {
      if (WERKSTATT_LABELS.includes(t as typeof WERKSTATT_LABELS[number])) return;
      map.set(t, (map.get(t) || 0) + 1);
    }));
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [kunden]);

  const stammkundenCount = useMemo(
    () => kunden.filter(k => (k.tags || []).includes(STAMMKUNDE_TAG)).length,
    [kunden],
  );

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return kunden.filter(k => {
      if (stammkundeOnly && !(k.tags || []).includes(STAMMKUNDE_TAG)) return false;
      if (activeLabels.length > 0 && !activeLabels.every(l => (k.tags || []).includes(l))) return false;
      if (!q) return true;
      return (
        k.name?.toLowerCase().includes(q) ||
        k.firma?.toLowerCase().includes(q) ||
        k.email?.toLowerCase().includes(q) ||
        (k.telefon || '').toLowerCase().includes(q) ||
        (k.mobil || '').toLowerCase().includes(q)
      );
    });
  }, [kunden, debouncedSearch, stammkundeOnly, activeLabels]);

  const assignExisting = async (kontaktId: string) => {
    const existing = kunden.find(k => k.id === kontaktId);
    const { data: kontakt, error: selErr } = existing
      ? { data: existing, error: null }
      : await supabase.from('crm_kontakte').select('tags, betrieb').eq('id', kontaktId).single();
    if (selErr) throw selErr;

    const newTags = mergeTags(kontakt?.tags, WERKSTATT_LABELS);
    const { error: updErr } = await supabase
      .from('crm_kontakte')
      .update({ betrieb: 'werkstatt', tags: newTags, aktualisiert_am: new Date().toISOString() })
      .eq('id', kontaktId);
    if (updErr) throw updErr;
    await setzeWerkstattLabelsUndMetadata(kontaktId);
    toast.success('Kontakt als Werkstatt-Kunde markiert');
    refresh();
  };

  const createNew = async (data: Partial<CRMKontakt>) => {
    const payload = {
      ...data,
      betrieb: 'werkstatt',
      kategorie: data.kategorie || 'werkstatt',
      tags: mergeTags(data.tags, WERKSTATT_LABELS),
    };
    const { data: kontaktData, error: err } = await supabase
      .from('crm_kontakte')
      .insert(payload)
      .select('id')
      .single();
    if (err) throw err;
    await setzeWerkstattLabelsUndMetadata((kontaktData as any).id);
    // Toast wird bereits von NeuerKontaktDialog ('Kontakt erstellt') gefeuert.
    refresh();
  };

  const toggleLabel = (label: string) => {
    setActiveLabels(prev => prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]);
  };

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <NeuerWerkstattKundeDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        onAssignExisting={assignExisting}
        onCreateNew={createNew}
      />

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D97706] flex items-center justify-center">
            <Wrench className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Werkstatt-Kundenverwaltung</h1>
            <p className="text-xs text-gray-500">Alle Kunden der Werkstatt (LCS GmbH) — CRM-basiert</p>
          </div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-semibold text-white bg-[#D97706] hover:bg-[#B45309] transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Neuer Werkstatt-Kunde</span>
          <span className="sm:hidden">Neu</span>
        </button>
      </div>

      {/* Stats */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 sm:grid sm:grid-cols-3 sm:gap-3 sm:overflow-visible sm:pb-0 sm:mb-5">
        <div className="flex-shrink-0 sm:flex-shrink bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 min-w-[140px]">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50">
            <Users className="w-4 h-4 text-[#D97706]" />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">{kunden.length}</div>
            <div className="text-[11px] text-gray-500 font-medium">Gesamt</div>
          </div>
        </div>
        <div className="flex-shrink-0 sm:flex-shrink bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 min-w-[140px]">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50">
            <Star className="w-4 h-4 text-[#D97706]" />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">{stammkundenCount}</div>
            <div className="text-[11px] text-gray-500 font-medium">Stammkunden</div>
          </div>
        </div>
        <div className="flex-shrink-0 sm:flex-shrink bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 min-w-[140px]">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-amber-50">
            <Tag className="w-4 h-4 text-[#D97706]" />
          </div>
          <div>
            <div className="text-lg font-bold text-gray-900">{labelCounts.length}</div>
            <div className="text-[11px] text-gray-500 font-medium">Aktive Labels</div>
          </div>
        </div>
      </div>

      {/* Suche */}
      <div className="flex items-center gap-2 sm:gap-3 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Suche nach Name, E-Mail oder Telefon..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D97706]/20 focus:border-[#D97706]"
          />
        </div>
      </div>

      {/* Filter-Pills */}
      <div className="flex gap-1.5 sm:gap-2 overflow-x-auto sm:flex-wrap pb-2 mb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
        <button
          onClick={() => setStammkundeOnly(v => !v)}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors ${
            stammkundeOnly ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Star className="w-3 h-3" />
          Stammkunden
          <span className="ml-0.5 opacity-60">{stammkundenCount}</span>
        </button>
        {labelCounts.map(([label, count]) => {
          const active = activeLabels.includes(label);
          return (
            <button
              key={label}
              onClick={() => toggleLabel(label)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors ${
                active ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Tag className="w-3 h-3" />
              {label}
              <span className="ml-0.5 opacity-60">{count}</span>
            </button>
          );
        })}
        {(stammkundeOnly || activeLabels.length > 0) && (
          <button
            onClick={() => { setStammkundeOnly(false); setActiveLabels([]); }}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap text-gray-500 hover:bg-gray-100"
          >
            <X className="w-3 h-3" />
            Zurücksetzen
          </button>
        )}
      </div>

      <div className="text-xs text-gray-400 mb-2">
        {loading ? 'Lade Werkstatt-Kunden...' : `${filtered.length} Werkstatt-Kunde${filtered.length === 1 ? '' : 'n'}`}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-center py-12 text-red-600">
          <p className="text-sm">Fehler beim Laden: {error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <Wrench className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">
            {kunden.length === 0
              ? 'Noch keine Werkstatt-Kunden — legen Sie den ersten an.'
              : 'Keine Kunden passen zum Filter.'}
          </p>
        </div>
      ) : (
        <ul className="space-y-1" role="list" aria-label="Werkstatt-Kunden">
          {filtered.map(k => (
            <li key={k.id}>
              <div className="w-full flex items-center gap-3 p-2.5 sm:p-3 rounded-lg border border-transparent hover:bg-gray-50 hover:border-gray-200 transition-colors">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-bold flex-shrink-0 bg-[#D97706]">
                  {getInitials(k.name || 'W K')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-gray-900 truncate">{k.name || 'Unbenannt'}</span>
                    {(k.tags || []).includes(STAMMKUNDE_TAG) && (
                      <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">
                        <Star className="w-2.5 h-2.5" />
                        Stammkunde
                      </span>
                    )}
                  </div>
                  {k.firma && <div className="text-xs text-gray-500 truncate">{k.firma}</div>}
                  <div className="flex items-center gap-3 mt-0.5 text-[11px] text-gray-400 truncate">
                    {k.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{k.email}</span>}
                    {k.telefon && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{k.telefon}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {(k.tags || []).includes(LCS_BETRIEB_NAME) && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">
                      <CheckCircle2 className="w-2.5 h-2.5" />
                      {LCS_BETRIEB_NAME}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
